import { type Experiments } from '../experiments';

/**
 * Exp 255 — the pre-auth mini quiz (`miniQuiz`). Prospects enroll on its two
 * designated surfaces (homepage hamburger, cookbook landings) and keep the
 * assignment as guests after signup.
 */
export const FREEMIUM_MINI_QUIZ_EXPERIMENT_ID = 255;

/**
 * Exp 260 — re-engagement of existing pre-checkout guests
 * (`guestFreemiumReengagement`). Enrolled only by a backend batch, so it is
 * never in the enrollment cookie and never resolves for an anonymous visitor.
 */
export const FREEMIUM_REENGAGEMENT_EXPERIMENT_ID = 260;

/**
 * Exp 266 — freemium checkout conversion (`freemiumCheckoutConversion`).
 * Enrolled client-side on `/mini-quiz/loading/` just before signup, so it
 * can appear in the enrollment cookie, but no pre-auth surface reads it;
 * after signup variant 1 receives the same re-engagement UI as Exp 260
 * treatment.
 */
export const FREEMIUM_CHECKOUT_CONVERSION_EXPERIMENT_ID = 266;

/**
 * Every experiment whose treatment arm turns on the freemium guest
 * experience. Pass this to an SSR read so one `/customers/self/experiments/`
 * call resolves all of them.
 */
export const FREEMIUM_EXPERIMENT_IDS = [
  FREEMIUM_MINI_QUIZ_EXPERIMENT_ID,
  FREEMIUM_REENGAGEMENT_EXPERIMENT_ID,
  FREEMIUM_CHECKOUT_CONVERSION_EXPERIMENT_ID,
] as const satisfies readonly Experiments[];

export interface FreemiumVariants {
  variant255: number | undefined;
  variant260: number | undefined;
  variant266: number | undefined;
}

/**
 * Whether a visitor gets the freemium guest experience.
 *
 * A treatment arm of Exp 255, Exp 260, or Exp 266 is treatment. Control,
 * unenrolled, and excluded arms do not qualify.
 *
 * Variants are canonical (`0` = control / unenrolled / excluded, `> 0` =
 * treatment), as returned by `readExperimentEnrollmentsSSR` and by
 * `useExperimentFlags`' `variant`. This is the single definition shared by
 * the SSR seed and the client gates so the server HTML and the hydrated
 * render always agree.
 */
export function isFreemiumTreatment({
  variant255,
  variant260,
  variant266,
}: FreemiumVariants): boolean {
  return (
    (variant255 ?? 0) > 0 || (variant260 ?? 0) > 0 || (variant266 ?? 0) > 0
  );
}

/**
 * Whether a visitor is in an Exp 260 or Exp 266 treatment arm — the audience
 * for the authenticated guest re-engagement UI.
 *
 * Exp 260 wins for a guest who is also in an Exp 255 treatment arm: the
 * re-engagement UI renders for them too. The Exp 255 variant is accepted so
 * every freemium resolver reads the same pair, but it does not affect the
 * result.
 *
 * Variants are canonical (`0` = control / unenrolled / excluded, `> 0` =
 * treatment), as for `isFreemiumTreatment`.
 */
export function isFreemiumReengagementTreatment({
  variant260,
  variant266,
}: FreemiumVariants): boolean {
  return (variant260 ?? 0) > 0 || (variant266 ?? 0) > 0;
}

/**
 * `<body>` class the springroll SSR layouts stamp for an Exp 260/266 guest
 * (`isFreemiumReengagementTreatment` + a pre-checkout customer), so the
 * client gate `useFreemiumReengagementGuest` can seed itself from the
 * server HTML before the identify and experiment reads resolve. Shared here
 * so the server that writes it and the hook that reads it can't drift.
 */
export const FREEMIUM_REENGAGEMENT_GUEST_BODY_CLASS =
  'freemium-reengagement-guest';
